<template>
  <div class="assessment-section">
    <div
      v-for="(assessment, assessmentIndex) in assessments"
      :key="assessmentIndex"
      class="assessment-item"
    >
      <div class="assessment-header">
        <div
          class="assessment-title"
          @click="toggleAccordion(assessmentIndex)"
          :class="{ opened: openAssessmentIndex === assessmentIndex }"
        >
          <div
            class="d-flex ga-3 align-items-center"
            v-if="editingTitleIndex !== assessmentIndex"
          >
            <img src="/images/icon/assesment-title.svg" alt="Edit" />
            <span>{{ assessment.title }}</span>
          </div>
          <input
            v-else
            :ref="
              (el) => {
                if (el) titleInputRefs[assessmentIndex] = el;
              }
            "
            v-model="assessment.title"
            @blur="finishEditingTitle"
            @keyup.enter="finishEditingTitle"
            type="text"
            class="title-edit-input"
            @click.stop
          />

          <div>
            <button
              class="edit-title-btn"
              @click.stop="startEditingTitle(assessmentIndex)"
              v-if="editingTitleIndex !== assessmentIndex"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                ></path>
              </svg>
            </button>
            <span
              class="accordion-arrow"
              :class="{ 'is-open': openAssessmentIndex === assessmentIndex }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </div>
        </div>

        <button
          @click.stop="removeAssessment(assessmentIndex)"
          class="remove-assessment-btn"
          :title="`Remove Assessment ${assessmentIndex + 1}`"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6"></polyline>
            <path
              d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
            ></path>
            <line x1="10" y1="11" x2="10" y2="17"></line>
            <line x1="14" y1="11" x2="14" y2="17"></line>
          </svg>
        </button>
      </div>

      <div
        class="assessment-body"
        :class="{ open: openAssessmentIndex === assessmentIndex }"
      >
        <div class="assessment-list">
          <div
            v-for="(item, itemIndex) in assessment.items"
            :key="itemIndex"
            class="assessment-list-item"
          >
            <div class="list-item-content">
              <div class="item-header">
                <img :src="itemHeaders[itemIndex].icon" alt="Edit" />
                <span class="item-header-text">
                  {{ itemHeaders[itemIndex].text }}
                </span>
              </div>
              <div
                v-if="
                  editingItemIndex.assessment !== assessmentIndex ||
                  editingItemIndex.item !== itemIndex
                "
                class="item-text"
                @click="startEditingItem(assessmentIndex, itemIndex)"
              >
                {{ item.text }}
              </div>
              <input
                v-else
                :ref="
                  (el) => {
                    if (el) itemInputRef = el;
                  }
                "
                v-model="item.text"
                @blur="finishEditingItem"
                @keyup.enter="finishEditingItem"
                type="text"
                class="item-edit-input"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="add-assessment-container">
      <button @click="addAssessment" class="add-assessment-btn">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        <span>Add Assessment</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  nextTick,
  defineEmits,
  watch,
  defineExpose,
  defineProps,
} from "vue";
import { v4 as uuidv4 } from "uuid";

const props = defineProps({
  initialAssessments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["assessments-updated"]);

const assessments = ref(
  props.initialAssessments.length
    ? props.initialAssessments
    : [
        {
          title: "Assessment 1",
          items: [
            { text: "Assessment item 1" },
            { text: "Assessment item 2" },
            { text: "Assessment item 3" },
          ],
        },
      ]
);

const openAssessmentIndex = ref(0);
const editingTitleIndex = ref(null);
const editingItemIndex = ref({ assessment: null, item: null });
const titleInputRefs = ref({});
const itemInputRef = ref(null);

const itemHeaders = ref([
  {
    text: "Emerging",
    icon: "/images/icon/emerging.svg",
  },
  {
    text: "Developing",
    icon: "/images/icon/developing.svg",
  },
  {
    text: "Proficient",
    icon: "/images/icon/proficient.svg",
  },
]);

const toggleAccordion = (index) => {
  openAssessmentIndex.value =
    openAssessmentIndex.value === index ? null : index;
};

const addAssessment = () => {
  const newIndex = assessments.value.length;
  assessments.value.push({
    title: `Assessment ${newIndex + 1}`,
    items: [
      { text: "Assessment item 1" },
      { text: "Assessment item 2" },
      { text: "Assessment item 3" },
    ],
    id: uuidv4(),
  });
  openAssessmentIndex.value = newIndex;
};

const removeAssessment = (index) => {
  if (assessments.value.length <= 1) {
    // Don't remove the last assessment
    return;
  }
  assessments.value.splice(index, 1);
  if (
    openAssessmentIndex.value === index ||
    openAssessmentIndex.value >= assessments.value.length
  ) {
    openAssessmentIndex.value = assessments.value.length - 1;
  }
};

const startEditingTitle = (index) => {
  editingTitleIndex.value = index;
  nextTick(() => {
    const inputEl = titleInputRefs.value[index];
    if (inputEl) {
      inputEl.focus();
      inputEl.select();
    }
  });
};

const finishEditingTitle = () => {
  editingTitleIndex.value = null;
};

const startEditingItem = (assessmentIndex, itemIndex) => {
  editingItemIndex.value = { assessment: assessmentIndex, item: itemIndex };
  nextTick(() => {
    if (itemInputRef.value) {
      itemInputRef.value.focus();
      itemInputRef.value.select();
    }
  });
};

const finishEditingItem = () => {
  editingItemIndex.value = { assessment: null, item: null };
};

const updateFromParent = (newAssessments) => {
  if (newAssessments && newAssessments.length) {
    assessments.value = newAssessments;
  }
};

// Watch for changes to emit to parent
watch(
  assessments,
  (newValue) => {
    emit("assessments-updated", newValue);
  },
  { deep: true }
);

// Expose methods to parent component
defineExpose({
  updateFromParent,
});
</script>

<style scoped>
.assessment-section {
  padding: 10px;
}

.assessment-item {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.assessment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0;
}

.assessment-title {
  flex-grow: 1;
  font-weight: 600;
  padding: 14px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  font-size: var(--fs-big);
  background-color: var(--green-dark-1, #026843);
  color: white;
}

.assessment-title span {
  flex-grow: 1;
}

.title-edit-input {
  flex-grow: 1;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
  margin-right: 10px;
  font-size: 0.95em;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.title-edit-input:focus {
  border-color: var(--primary-blue, #0d6efd);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.edit-title-btn {
  background: none;
  border: none;
  padding: 5px;
  margin-left: 10px;
  cursor: pointer;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  opacity: 0.7;
  transition: all 0.2s ease;
}

.assessment-title:hover .edit-title-btn {
  opacity: 1;
}

.assessment-title.opened .edit-title-btn {
  color: white;
  opacity: 0.8;
}

.assessment-title.opened:hover .edit-title-btn {
  opacity: 1;
}

.edit-title-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.accordion-arrow {
  margin-left: 10px;
  transition: transform 0.3s ease;
  color: white;
}

.accordion-arrow.is-open {
  transform: rotate(180deg);
}

.remove-assessment-btn {
  background: none;
  border: none;
  padding: 14px 18px;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.assessment-header:hover .remove-assessment-btn {
  opacity: 1;
}

.assessment-body {
  max-height: 0;
  overflow: hidden;
  transition:
    max-height 0.4s ease,
    opacity 0.3s ease;
  opacity: 0;
}

.assessment-body.open {
  max-height: 1000px;
  opacity: 1;
}

.assessment-list-item {
  padding: 10px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f1f3f5;
  transition: all 0.2s ease;
  margin-top: 10px;
  background-color: #f8f9fa;
}

.assessment-list-item:hover {
  background-color: #f2f3fb;
  border-radius: 6px;
}

.assessment-list-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.list-item-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 8px;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.2rem;
}

.item-header-text {
  font-size: 1.2rem;
  font-weight: 600;
}

.item-header .item-text {
  flex-grow: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.item-text:hover {
  background-color: #e9ecef;
}

.item-edit-input {
  flex-grow: 1;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  width: 100%;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.item-edit-input:focus {
  border-color: var(--primary-blue, #0d6efd);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.add-assessment-container {
  margin-top: 20px;
  text-align: center;
}

.add-assessment-btn {
  background-color: #f8f9fa;
  border: 1px dashed #ced4da;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  color: var(--primary-blue, #0d6efd);
  font-weight: 500;
}

.add-assessment-btn:hover {
  background-color: #e9ecef;
  border-color: var(--primary-blue, #0d6efd);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.add-assessment-btn svg {
  transition: transform 0.3s ease;
}

.add-assessment-btn:hover svg {
  transform: rotate(90deg);
}
</style>
